import { useTranslation } from "react-i18next";
import { Download } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@cieda/djayma-ui";
import type { Product } from "@/lib/validation/product";

interface BulkExportModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly products: readonly Product[];
  /**
   * Triggers the CSV download. The page should wire this to
   * `productsApi.exportCsv(filters)` and hand off the resulting Blob
   * to a download helper.
   */
  readonly onConfirm: () => void;
  readonly isLoading: boolean;
}

/**
 * Confirmation + trigger for bulk CSV export of the currently selected
 * products. Actual download is performed by the caller via
 * `productsApi.exportCsv`.
 */
export function BulkExportModal({
  open,
  onOpenChange,
  products,
  onConfirm,
  isLoading,
}: BulkExportModalProps) {
  const { t } = useTranslation("admin");
  const preview = products.slice(0, 5);
  const remaining = products.length - preview.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            {t("products.bulk.export_csv")} ({products.length} produit(s))
          </DialogTitle>
        </DialogHeader>
        <ul className="max-h-48 space-y-1 overflow-y-auto text-sm text-stone-700">
          {preview.map((p) => (
            <li key={p.id}>{p.name}</li>
          ))}
          {remaining > 0 && (
            <li className="text-stone-400">et {remaining} autre(s)</li>
          )}
        </ul>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={onConfirm} disabled={isLoading}>
            <Download className="mr-1.5 h-4 w-4" />
            {t("products.bulk.export_csv")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
